/* @flow */
import React, { Component } from "react";
import PropTypes from "prop-types";
import styled, { css } from "styled-components";
import Gettext from "../common/Gettext";
import ClickOutside from "../common/ClickOutside";
import { fontSize } from "../common/styled-mixins";

const DropdownButton = styled.div`
  position: absolute;
  top: 50%;
  right: 9px;
  transform: translateY(-50%);
  border: 4px solid transparent;
  border-top-color: #99959d;
  border-bottom: none;
  z-index: 20;
`;

const SelectView = styled.div`
  position: relative;
`;

const Value = styled.div`
  position: relative;
  width: 100%;
  padding: 6px 9px 5px;
  font-size: ${fontSize(16)};
  color: ${({ theme }) => theme.foreground};
  border: 1px solid ${props => props.theme.borderMainColor};
  border-radius: 4px;
  background: ${props => props.theme.mainDark};
  box-shadow: inset 0px 3px 0px 0px rgba(0, 0, 0, 0.08);
  appearance: none;
  cursor: pointer;

  ${({ tourBorder }) =>
    tourBorder &&
    css`
      ${tourBorder.border};
    `};

  ${({ tourBorderAnimation }) =>
    tourBorderAnimation &&
    tourBorderAnimation.animation(tourBorderAnimation.keyframe)};

  @media (max-width: 959px) {
    padding: 5px 8px 5px;
  }
`;

const OptionList = styled.div`
  position: absolute;
  top: 100%;
  width: 100%;
  overflow: auto;
  margin-top: -2px;
  padding: 8px 0;
  font-size: ${fontSize(16)};
  color: ${({ theme }) => theme.foreground};
  background: ${props => props.theme.mainDark};
  border-top: 1px solid #696969;
  z-index: 1337;

  @media (max-width: 959px) {
    font-size: ${fontSize(16)};
    max-height: 85px;
  }

  @media (max-width: 479px) {
    top: auto;
    bottom: 100%;
  }
`;

const OptionView = styled.div`
  padding: 4px 10px;
  cursor: pointer;

  :hover {
    background: rgba(255, 255, 255, 0.1);
  }

  ${props =>
    props.active &&
    css`
      background: rgba(255, 255, 255, 0.1);
    `};

  @media (max-width: 959px) {
    padding: 5px 8px 5px;
    font-size: ${fontSize(16)};
  }
`;

type SelectProps<V> = {
  value: V,
  onChange: (value: V) => void,
  currentInterval?: number,
  children?: Function
};

type Context<V> = {
  selectValue: V,
  onChange: (value: V) => void
};

const contextTypes = {
  value: PropTypes.any,
  onChange: PropTypes.func,
  currentInterval: PropTypes.number
};

function toggle(state) {
  return { isOpen: !state.isOpen };
}

type SelectState = {
  isOpen: boolean
};

export default class Select extends Component<SelectProps<*>, SelectState> {
  static childContextTypes = contextTypes;

  state = {
    isOpen: false
  };

  handleChange = (nextValue: any) => {
    this.setState({
      isOpen: false
    });
    this.props.onChange(nextValue);
  };

  collapse = () => {
    this.setState({ isOpen: false });
  };

  getChildContext() {
    return {
      value: this.props.value,
      onChange: this.handleChange,
      currentInterval: this.props.currentInterval
    };
  }

  render() {
    const { value, children, currentInterval } = this.props;
    const { isOpen } = this.state;

    const options =
      isOpen && typeof children === "function" ? children() : null;

    return (
      <ClickOutside onOutsideClick={this.collapse}>
        {({ innerRef }) => (
          <SelectView ref={innerRef}>
            <DropdownButton />
            <Value
              tourBorder={
                !this.props.isOriginal &&
                this.props.tour &&
                this.props.tour.borderStyles
              }
              tourBorderAnimation={
                !this.props.isOriginal &&
                this.props.tour &&
                this.props.tour.outside &&
                this.props.tour.borderStyles
              }
              onClick={() => {
                this.setState(toggle);
              }}
            >
              {value}{" "}
              {currentInterval && currentInterval < 60 ? (
                <Gettext>sec</Gettext>
              ) : currentInterval < 120 || currentInterval > 600 ? (
                <Gettext>min</Gettext>
              ) : (
                <Gettext>mins</Gettext>
              )}
            </Value>
            {options}
          </SelectView>
        )}
      </ClickOutside>
    );
  }
}

type OptionListProps = {
  children?: any
};

type OptionProps = {
  value?: any,
  children?: any
};

export function Options({ children }: OptionListProps) {
  return <OptionList>{children}</OptionList>;
}

export function Option(
  { children, value }: OptionProps,
  { selectValue, onChange }: Context<*>
) {
  const meaningfulValue = typeof value !== "undefined" ? value : children;

  const isActive = meaningfulValue === selectValue;

  return (
    <OptionView
      active={isActive}
      onClick={() => {
        onChange(meaningfulValue);
      }}
    >
      {children}
    </OptionView>
  );
}

Option.contextTypes = contextTypes;



// WEBPACK FOOTER //
// ./src/components/TradeControls/Select.js